import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";

export type BadgeTone =
  | "neutral"
  | "gold"
  | "crimson"
  | "success"
  | "warning"
  | "danger"
  | "info";

const tones: Record<BadgeTone, string> = {
  neutral:
    "bg-surface-sunken text-content-muted border-line-strong/60",
  gold: "bg-gold-100 text-gold-800 border-gold-300/70 dark:bg-gold-950/60 dark:text-gold-300 dark:border-gold-800",
  crimson:
    "bg-crimson-50 text-crimson-700 border-crimson-200 dark:bg-crimson-950/50 dark:text-crimson-300 dark:border-crimson-900",
  success:
    "bg-success-50 text-success-700 border-success-500/25 dark:bg-success-500/10 dark:text-success-500 dark:border-success-500/30",
  warning:
    "bg-warning-50 text-warning-700 border-warning-500/25 dark:bg-warning-500/10 dark:text-warning-500 dark:border-warning-500/30",
  danger:
    "bg-danger-50 text-danger-700 border-danger-500/25 dark:bg-danger-500/10 dark:text-danger-500 dark:border-danger-500/30",
  info: "bg-info-50 text-info-700 border-info-500/25 dark:bg-info-500/10 dark:text-info-500 dark:border-info-500/30",
};

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  icon?: LucideIcon;
  /** Small filled circle instead of an icon — good for status lists. */
  dot?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const dotTones: Record<BadgeTone, string> = {
  neutral: "bg-ink-400",
  gold: "bg-gold-500",
  crimson: "bg-crimson-600",
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  info: "bg-info-500",
};

export function Badge({
  children,
  tone = "neutral",
  icon: Icon,
  dot = false,
  size = "sm",
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border font-medium",
        size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm",
        tones[tone],
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full", dotTones[tone])}
        />
      )}
      {Icon && <Icon size={size === "sm" ? 12 : 14} aria-hidden="true" />}
      {children}
    </span>
  );
}

/**
 * Quote lifecycle states, mapped once so every table and detail page agrees on
 * what colour "Failed" is.
 */
const QUOTE_STATE_TONES: Record<string, BadgeTone> = {
  quoted: "success",
  partial: "info",
  paid: "success",
  processed: "info",
  pending: "warning",
  "in process": "warning",
  "queued for delivery": "warning",
  "delivered successfully": "success",
  cancelled: "neutral",
  failed: "danger",
  declined: "danger",
  error: "danger",
  referred: "warning",
};

export function StatusPill({
  status,
  className,
}: {
  status: string | null | undefined;
  className?: string;
}) {
  const label = status?.trim() || "Unknown";
  const tone = QUOTE_STATE_TONES[label.toLowerCase()] ?? "neutral";
  return (
    <Badge tone={tone} dot className={className}>
      {label}
    </Badge>
  );
}
